import { api } from './apiClient';
import { SEED_MEDITATION_ACHIEVEMENTS_CONFIRM } from './meditationAchievementsConstants';

export {
  DEFERRED_BADGE_NUMBERS,
  SEED_MEDITATION_ACHIEVEMENTS_CONFIRM,
} from './meditationAchievementsConstants';

/**
 * Meditation Achievements (Dhyaan Badges) Admin API
 * Base: `/api/v1/admin/meditation/achievements`
 *
 * Verified against Vedify-Backend:
 * - controllers/admin/meditationAchievements.js
 * - routes/admin/v1.js
 *
 * list/detail/enums, create, update, guarded seed.
 * Launch Configuration (GET/PUT …/launch-config) retired — Backend and Admin.
 */

const BASE = '/api/v1/admin/meditation/achievements';

export const formatMeditationAchievementError = (
  error,
  fallback = 'Request failed'
) => {
  const data = error?.response?.data;
  if (!data) {
    if (error?.request) {
      return 'Unable to reach the server. Please check your internet connection or try again later.';
    }
    return error?.message || fallback;
  }
  if (Array.isArray(data.errors) && data.errors.length) {
    const head = data.message || 'Validation failed';
    return [head, ...data.errors].join('\n');
  }
  if (typeof data.errors === 'string' && data.errors) {
    return data.message && data.message !== data.errors
      ? `${data.message}\n${data.errors}`
      : data.errors;
  }
  return data.message || fallback;
};

const request = async (fn, fallback) => {
  try {
    const response = await fn();
    return response.data;
  } catch (error) {
    const err = new Error(formatMeditationAchievementError(error, fallback));
    err.payload = error?.response?.data;
    err.status = error?.response?.status;
    throw err;
  }
};

const pathId = (id) => encodeURIComponent(String(id));

/**
 * GET …/meta/enums
 * @returns {Promise<{ status, message, data: { categories, trigger_types, rarities, … } }>}
 */
export const getMeditationAchievementEnums = async () =>
  request(
    () => api.get(`${BASE}/meta/enums`),
    'Failed to load meditation achievement enums'
  );

/**
 * POST …/list
 * Body: { searchtext?, pagination?: { page, pageSize, sort, filters, projection } }
 * Backend getManual uses page + pageSize (not limit).
 */
export const listMeditationAchievements = async (body = {}) =>
  request(
    () => api.post(`${BASE}/list`, body),
    'Failed to load meditation achievements'
  );

/**
 * GET …/:achievement_id
 */
export const getMeditationAchievement = async (achievementId) =>
  request(
    () => api.get(`${BASE}/${pathId(achievementId)}`),
    'Failed to load meditation achievement'
  );

/**
 * POST …/achievements
 * Create requires logo_url URI among other Joi fields.
 */
export const createMeditationAchievement = async (payload) =>
  request(
    () => api.post(BASE, payload),
    'Failed to create meditation achievement'
  );

/**
 * PUT …/achievements
 * Body must include achievement_id. `code` is forbidden by Backend Joi.
 */
export const updateMeditationAchievement = async (payload) =>
  request(
    () => api.put(BASE, payload),
    'Failed to update meditation achievement'
  );

/**
 * POST …/seed
 * Body: { confirm: "SEED_MEDITATION_ACHIEVEMENTS" }
 * Backend returns 403 when NODE_ENV=production.
 * Callers must require explicit Admin confirmation — never auto-seed.
 */
export const seedMeditationAchievements = async (
  confirm = SEED_MEDITATION_ACHIEVEMENTS_CONFIRM
) =>
  request(
    () => api.post(`${BASE}/seed`, { confirm }),
    'Failed to seed meditation achievements'
  );
